import { stableRecordKey } from '../../lib/rowIdentity';
import { retainRowIdentity } from '../../lib/rowIdentity';
import React, { useState, useEffect, useCallback } from 'react';
import { Save, Eye, Upload, Trash2, Plus, History, RotateCcw, Loader2, ExternalLink, ImageIcon, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { toast } from 'sonner';
import { api } from '../../App';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const fmt = (d) => d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

/* ============================================================
   PAGE CONTENT EDITOR (spec P5.1) — ADMIN ONLY
   Draft -> Preview (public page with ?preview=draft) -> Publish. Revisions restorable into draft.
   ============================================================ */
export function ContentEditorTab({ token, role }) {
  const isAdmin = role === 'admin';
  const [pages, setPages] = useState([]);
  const [pageKey, setPageKey] = useState('home');
  const [data, setData] = useState(null);       // {schema, defaults, published, draft, ...}
  const [form, setForm] = useState({});         // working copy (defaults <- published <- draft)
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);

  const loadPages = useCallback(() => api.getAuth('/api/admin/content', token).then(d => setPages(d.pages || [])).catch(() => {}), [token]);
  const loadPage = useCallback(async (key) => {
    setData(null);
    try {
      const d = await api.getAuth(`/api/admin/content/${key}`, token);
      setData(d);
      setForm({ ...d.defaults, ...d.published, ...d.draft });
      setDirty(false);
    } catch (e) { toast.error(e.message); }
  }, [token]);

  useEffect(() => { loadPages(); }, [loadPages]);
  useEffect(() => { loadPage(pageKey); }, [pageKey, loadPage]);

  const setField = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); };

  const saveDraft = async () => {
    setSaving(true);
    try {
      await api.putAuth(`/api/admin/content/${pageKey}/draft`, { data: form }, token);
      toast.success('Draft saved — not live yet. Use Preview to check it, then Publish.');
      await loadPage(pageKey); loadPages();
      return true;
    } catch (e) { toast.error(e.message); return false; }
    finally { setSaving(false); }
  };
  const preview = async () => {
    if (dirty) { const ok = await saveDraft(); if (!ok) return; }
    const path = data?.schema?.path || '/';
    window.open(`${window.location.origin}${path}${path.includes('?') ? '&' : '?'}preview=draft`, '_blank', 'noopener');
  };
  const publish = async () => {
    try {
      if (dirty) { const ok = await saveDraft(); if (!ok) return; }
      const r = await api.postAuth(`/api/admin/content/${pageKey}/publish`, {}, token);
      toast.success(r.message);
      setPublishOpen(false);
      await loadPage(pageKey); loadPages();
    } catch (e) { toast.error(e.message); }
  };
  const discard = async () => {
    try {
      await api.postAuth(`/api/admin/content/${pageKey}/discard`, {}, token);
      toast.success('Draft discarded — showing the published version');
      await loadPage(pageKey); loadPages();
    } catch (e) { toast.error(e.message); }
  };
  const restore = async (revision_id) => {
    try {
      const r = await api.postAuth(`/api/admin/content/${pageKey}/revert`, { revision_id }, token);
      toast.success(r.message);
      await loadPage(pageKey); loadPages();
    } catch (e) { toast.error(e.message); }
  };

  if (!isAdmin) {
    return (
      <div className="p-10 text-center border rounded-xl" data-testid="content-editor-forbidden">
        <p className="font-medium">Page content editing is restricted to Administrators.</p>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">You can still upload photos and videos in the File Manager.</p>
      </div>
    );
  }

  const hasDraft = !!data?.has_draft || dirty;

  return (
    <div data-testid="content-editor-tab">
      <div className="flex flex-col lg:flex-row lg:items-start gap-6">
        {/* Page list */}
        <Card className="lg:w-64 shrink-0">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" /> Pages</CardTitle></CardHeader>
          <CardContent className="p-2">
            {pages.map(p => (
              <button key={p.page} onClick={() => setPageKey(p.page)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between transition-colors ${pageKey === p.page ? 'bg-[hsl(var(--primary))] text-white' : 'hover:bg-[hsl(var(--muted))]'}`}
                data-testid={`content-page-${p.page}`}>
                <span>{p.label}</span>
                {p.has_draft && <span className={`text-[10px] px-1.5 py-0.5 rounded ${pageKey === p.page ? 'bg-white/20' : 'bg-amber-100 text-amber-800'}`}>draft</span>}
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Editor */}
        <div className="flex-1 min-w-0 space-y-5">
          {!data ? <div className="animate-pulse h-64 bg-[hsl(var(--muted))] rounded-xl" /> : (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{data.schema.label} page</h2>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] flex flex-wrap gap-x-3 gap-y-1 mt-1">
                    <span>Published: {data.published_at ? `${fmt(data.published_at)} by ${data.published_by || ''}` : 'never (built-in text showing)'}</span>
                    {data.has_draft && <span className="text-amber-700">Draft saved {fmt(data.draft_updated_at)} by {data.draft_updated_by}</span>}
                    {dirty && <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700">unsaved changes</Badge>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={saveDraft} disabled={saving} data-testid="content-save-draft"><Save className="w-4 h-4 mr-1" />{saving ? 'Saving…' : 'Save draft'}</Button>
                  <Button variant="outline" size="sm" onClick={preview} disabled={saving} data-testid="content-preview"><Eye className="w-4 h-4 mr-1" /> Preview</Button>
                  <Button size="sm" onClick={() => setPublishOpen(true)} disabled={saving || !hasDraft} data-testid="content-publish"><Upload className="w-4 h-4 mr-1" /> Publish</Button>
                  {data.has_draft && <Button variant="ghost" size="sm" onClick={discard} data-testid="content-discard"><Trash2 className="w-4 h-4 mr-1" /> Discard draft</Button>}
                </div>
              </div>

              {data.schema.sections.map(sec => (
                <Card key={sec.title}>
                  <CardHeader className="pb-3"><CardTitle className="text-base">{sec.title}</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    {sec.fields.map(f => (
                      <FieldEditor key={f.key} field={f} value={form[f.key]} onChange={v => setField(f.key, v)} token={token} />
                    ))}
                  </CardContent>
                </Card>
              ))}

              {data.revisions?.length > 0 && (
                <Card>
                  <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4" /> Previously published versions</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    {data.revisions.map(r => (
                      <div key={r.revision_id} className="flex items-center justify-between text-sm border rounded-md px-3 py-2" data-testid={`content-revision-${r.revision_id}`}>
                        <span>{fmt(r.published_at)} <span className="text-[hsl(var(--muted-foreground))]">by {r.published_by}</span></span>
                        <Button size="sm" variant="outline" onClick={() => restore(r.revision_id)}><RotateCcw className="w-3.5 h-3.5 mr-1" /> Load into draft</Button>
                      </div>
                    ))}
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">Restoring loads the old version as a draft so you can preview it before publishing.</p>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent data-testid="content-publish-dialog">
          <DialogHeader>
            <DialogTitle>Publish {data?.schema?.label} page?</DialogTitle>
            <DialogDescription>Your draft will replace the live content immediately for all visitors. The previous version is kept and can be restored.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPublishOpen(false)}>Cancel</Button>
            <Button onClick={publish} data-testid="content-publish-confirm"><Upload className="w-4 h-4 mr-1" /> Publish now</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FieldEditor({ field: f, value, onChange, token }) {
  const id = `cf-${f.key}`;
  const help = f.help ? <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{f.help}</p> : null;
  if (f.managed === 'org') {
    return (
      <div data-testid={`content-field-managed-${f.key}`}>
        <Label htmlFor={id} className="flex items-center gap-1.5">{f.label} <Badge variant="outline" className="text-[10px] font-normal">Organisation setting</Badge></Label>
        <Input id={id} value={value ?? ''} readOnly disabled className="mt-1 bg-[hsl(var(--muted)/0.4)]" data-testid={`content-field-${f.key}`} />
        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Managed in <a href="/admin?tab=settings" className="underline">Settings → Organisation</a> — change it there and every page updates.</p>
      </div>
    );
  }
  switch (f.type) {
    case 'toggle':
      return (
        <div className="flex items-center justify-between p-3 rounded-lg border">
          <div><div className="font-medium text-sm">{f.label}</div>{help}</div>
          <Switch checked={!!value} onCheckedChange={onChange} data-testid={`content-field-${f.key}`} />
        </div>
      );
    case 'textarea':
    case 'paragraphs':
      return (
        <div>
          <Label htmlFor={id}>{f.label}</Label>
          <Textarea id={id} rows={f.type === 'paragraphs' ? 8 : 3} value={value ?? ''} onChange={e => onChange(e.target.value)} data-testid={`content-field-${f.key}`} className="mt-1" />
          {help}
        </div>
      );
    case 'image':
      return <ImageField id={id} label={f.label} value={value ?? ''} onChange={onChange} token={token} help={help} testId={`content-field-${f.key}`} />;
    case 'list':
      return <ListField field={f} value={Array.isArray(value) ? value : []} onChange={onChange} token={token} />;
    default:
      return (
        <div>
          <Label htmlFor={id}>{f.label}</Label>
          <Input id={id} value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder={f.type === 'url' ? 'https://… or /page' : ''} data-testid={`content-field-${f.key}`} className="mt-1" />
          {help}
        </div>
      );
  }
}

function ListField({ field: f, value, onChange, token }) {
  const setItem = (i, k, v) => onChange(value.map((it, idx) => idx === i ? retainRowIdentity(it, { ...it, [k]: v }) : it));
  const add = () => { if (value.length >= (f.max_items || 8)) { toast.error(`Maximum ${f.max_items} items`); return; } onChange([...value, Object.fromEntries(f.item_fields.map(sf => [sf.key, '']))]); };
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= value.length) return; const arr = [...value]; [arr[i], arr[j]] = [arr[j], arr[i]]; onChange(arr); };
  return (
    <div data-testid={`content-field-${f.key}`}>
      <div className="flex items-center justify-between mb-2">
        <Label>{f.label} <span className="text-xs text-[hsl(var(--muted-foreground))]">({value.length}/{f.max_items || 8})</span></Label>
        <Button type="button" size="sm" variant="outline" onClick={add} data-testid={`content-list-add-${f.key}`}><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
      </div>
      {f.help && <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2">{f.help}</p>}
      <div className="space-y-2">
        {value.map((it, i) => (
          <div key={stableRecordKey(it)} className="p-3 rounded-lg border grid gap-2 sm:grid-cols-[1fr_auto]">
            <div className="grid gap-2 sm:grid-cols-2">
              {f.item_fields.map(sf => sf.type === 'image'
                ? <div key={sf.key} className="sm:col-span-2"><ImageField id={`${f.key}-${i}-${sf.key}`} label={sf.label} value={it[sf.key] ?? ''} onChange={v => setItem(i, sf.key, v)} token={token} compact /></div>
                : <div key={sf.key}><Label className="text-xs">{sf.label}</Label><Input value={it[sf.key] ?? ''} onChange={e => setItem(i, sf.key, e.target.value)} className="mt-1 h-9" /></div>)}
            </div>
            <div className="flex sm:flex-col gap-1 justify-end">
              <Button type="button" size="icon" variant="ghost" onClick={() => move(i, -1)} aria-label="Move up" className="h-8 w-8">↑</Button>
              <Button type="button" size="icon" variant="ghost" onClick={() => move(i, 1)} aria-label="Move down" className="h-8 w-8">↓</Button>
              <Button type="button" size="icon" variant="ghost" onClick={() => remove(i)} aria-label="Remove" className="h-8 w-8"><Trash2 className="w-4 h-4 text-[hsl(var(--destructive))]" /></Button>
            </div>
          </div>
        ))}
        {value.length === 0 && <p className="text-xs text-[hsl(var(--muted-foreground))] italic">No items — the built-in default list is shown on the site.</p>}
      </div>
    </div>
  );
}

/* Image URL with a media-library picker (files uploaded by any staff role in File Manager) */
function ImageField({ id, label, value, onChange, token, help, compact, testId }) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState(null);
  const [uploading, setUploading] = useState(false);
  const load = () => api.getAuth('/api/admin/files', token).then(d => setFiles((d.files || []).filter(f => /image/.test(f.content_type || '') || /\.(png|jpe?g|webp|gif)$/i.test(f.original_name || f.filename || '')))).catch(() => setFiles([]));
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.uploadAuth('/api/admin/upload?category=general', fd, token);
      onChange(r.url || `/api/files/${r.file_id}`); setOpen(false); toast.success('Image uploaded');
    } catch (err) { toast.error(err.message); } finally { setUploading(false); }
  };
  const abs = (u) => u && u.startsWith('/') ? `${BACKEND}${u}` : u;
  return (
    <div>
      <Label htmlFor={id} className={compact ? 'text-xs' : ''}>{label}</Label>
      <div className="flex gap-2 mt-1 items-start">
        {value ? <img src={abs(value)} alt="" className="w-14 h-14 rounded object-cover border shrink-0" /> : <div className="w-14 h-14 rounded border flex items-center justify-center text-[hsl(var(--muted-foreground))] shrink-0"><ImageIcon className="w-5 h-5" /></div>}
        <div className="flex-1 space-y-1">
          <Input id={id} value={value} onChange={e => onChange(e.target.value)} placeholder="https://… or /api/files/…" data-testid={testId} className="h-9" />
          <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o && files === null) load(); }}>
            <PopoverTrigger asChild><Button type="button" size="sm" variant="outline"><ImageIcon className="w-3.5 h-3.5 mr-1" /> Choose from media library</Button></PopoverTrigger>
            <PopoverContent className="w-80 max-h-80 overflow-y-auto" align="start">
              <label className="flex items-center gap-2 text-sm cursor-pointer border rounded-md px-3 py-2 mb-2 hover:bg-[hsl(var(--muted))]">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload new image
                <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={uploading} />
              </label>
              {files === null ? <div className="text-xs text-[hsl(var(--muted-foreground))]">Loading…</div> : files.length === 0 ? <div className="text-xs text-[hsl(var(--muted-foreground))]">No images in the library yet.</div> : (
                <div className="grid grid-cols-3 gap-2">
                  {files.map(f => (
                    <button type="button" key={f.file_id} onClick={() => { onChange(f.url || `/api/files/${f.file_id}`); setOpen(false); }} className="aspect-square rounded overflow-hidden border hover:ring-2 ring-[hsl(var(--primary))]" title={f.original_name || f.filename}>
                      <img src={abs(f.url || `/api/files/${f.file_id}`)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </PopoverContent>
          </Popover>
        </div>
        {value && <a href={abs(value)} target="_blank" rel="noreferrer" className="text-[hsl(var(--muted-foreground))] mt-2" aria-label="Open image"><ExternalLink className="w-4 h-4" /></a>}
      </div>
      {help}
    </div>
  );
}
